import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { OrdersService } from '../../services/orders.service';
import { catchError, combineLatest, EMPTY, filter, map, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { DatePipe, isPlatformBrowser, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { OrderItemModalComponent } from '../order-item-modal/order-item-modal.component';
import {
  EDIT_STATUS_DIALOG_CONFIG,
  FAKE_ORDERS_QUANTITY,
  ORDER_TABLE_COLUMNS_CONFIG,
} from '@app/utils/constants/orders.constants';
import {
  Order,
  OrderPaginatedResponse,
  OrdersQueryParams,
} from '@app/utils/interfaces/order.interface';
import { TablePaginationComponent } from '@app/components/table-pagination/table-pagination.component';
import { TABLE_PAGINATION_DEFAULT_CONFIG } from '@app/utils/constants/table-pagination.constants';
import { TablePaginationConfig } from '@app/utils/interfaces/table-pagination.interface';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { InventoryTableSearchComponent } from '@app/pages/inventory/components/inventory-table-functions/inventory-table-search/inventory-table-search.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { OrderColumnKey } from '@app/utils/enums/order-table-columns.enum';
import { EditStatusDialogComponent } from '../edit-status-dialog/edit-status-dialog.component';
import { OrderFiltersComponent } from '../order-filters/order-filters.component';
import { RouterLink } from '@angular/router';
import { OrderFilterService } from '../../services/order-filter/order-filter.service';
import { FilterChipComponent } from '../filter-chip/filter-chip.component';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { SortedOptions } from '@app/utils/interfaces/inventory-item.interface';

@Component({
  selector: 'app-orders-table',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    TranslatePipe,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    TablePaginationComponent,
    MatTooltipModule,
    InventoryTableSearchComponent,
    IconComponent,
    NgClass,
    OrderFiltersComponent,
    RouterLink,
    FilterChipComponent,
  ],
  templateUrl: './orders-table.component.html',
  styleUrl: './orders-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrdersTableComponent implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderService = inject(OrdersService);
  private readonly ordersFilterService = inject(OrderFilterService);
  private readonly dialog = inject(MatDialog);
  private readonly sso = inject(ScrollStrategyOptions);

  private styleElement: HTMLStyleElement | null = null;

  public readonly iconName = IconName;
  public readonly orderColumnKey = OrderColumnKey;

  public readonly skeletonRows = signal<Partial<Order>[]>(new Array(FAKE_ORDERS_QUANTITY).fill({}));

  public readonly ordersTableColumnsConfig = signal(ORDER_TABLE_COLUMNS_CONFIG);
  public readonly ordersTableColumnKeys = ORDER_TABLE_COLUMNS_CONFIG.map((c) => c.key);
  public readonly isFilterOpen = signal<boolean>(false);

  public readonly ordersPagination = signal<TablePaginationConfig>(TABLE_PAGINATION_DEFAULT_CONFIG);
  public readonly showPagination = computed(() => Boolean(this.ordersPagination().totalItems));

  private readonly platformId = inject(PLATFORM_ID);

  public readonly activeFilters = toSignal(
    this.ordersFilterService.activeFilters$.pipe(takeUntilDestroyed(this.destroyRef)),
    { initialValue: { SortingOptions: [{ field: 'Id', isDescending: true }] } },
  );

  public readonly isAnyFilterApplied = toSignal(
    this.ordersFilterService.hasActiveFilters$.pipe(takeUntilDestroyed(this.destroyRef)),
    { initialValue: false },
  );

  public readonly ordersSort = signal<SortedOptions[]>([]);

  public readonly ordersLoading = signal(false);

  private readonly ordersResponse$ = combineLatest({
    filters: this.ordersFilterService.activeFilters$,
    pagination: toObservable(this.ordersPagination),
    sort: toObservable(this.ordersSort),
  }).pipe(
    tap(() => this.ordersLoading.set(true)),
    switchMap(({ filters, pagination, sort }) => {
      const requestPayload: OrdersQueryParams = {
        pageNumber: pagination.currentPage,
        pageSize: pagination.pageSize,
        sortingOptions: [...sort],
        ...filters,
      };

      return this.orderService.getOrders(requestPayload).pipe(
        catchError(() => {
          this.handleOpenErrorModal();
          return EMPTY;
        }),
      );
    }),
    tap((response: OrderPaginatedResponse) => {
      const newTotal = response.data?.totalAmount ?? 0;

      if (this.ordersPagination().totalItems !== newTotal) {
        this.ordersPagination.update((prev) => ({
          ...prev,
          totalItems: newTotal,
        }));
      }
      this.ordersLoading.set(false);
    }),
  );

  public readonly orders = toSignal(
    this.ordersResponse$.pipe(map((response) => response.data?.items ?? [])),
    { initialValue: [] as Order[] },
  );

  public openEditOrderStatus(order: Order): void {
    const dialogRef = this.dialog.open(EditStatusDialogComponent, {
      ...EDIT_STATUS_DIALOG_CONFIG,
      data: { order },
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.ordersPagination.update((p) => ({ ...p }));
      });
  }

  public handlePageChange(page: number): void {
    this.updatePagination({ currentPage: page });
  }

  public handlePageSizeChange(pageSize: number): void {
    this.updatePagination({ pageSize, currentPage: 1 });
  }

  public formatOrderStatus(status: string): string {
    return status.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  protected isModalOpen = signal(false);

  public openViewOrder(order: Order): void {
    this.isModalOpen.set(true);

    const dialogRef = this.dialog.open(OrderItemModalComponent, {
      data: { mode: 'view', id: order.id },
      scrollStrategy: this.sso.block(),
    });

    dialogRef.afterClosed().subscribe(() => {
      this.removeScrollbarFix();
      this.isModalOpen.set(false);
    });
  }

  private removeScrollbarFix(): void {
    if (isPlatformBrowser(this.platformId) && this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
  }

  public toggleFilterPanel(isOpen: boolean): void {
    this.isFilterOpen.set(isOpen);
  }

  public handleFiltersApplied(): void {
    this.ordersPagination.update((p) => ({ ...p, currentPage: 1 }));
    this.toggleFilterPanel(false);
  }

  public handleSortApplied(event: SortedOptions[]): void {
    this.ordersSort.set(event)
    this.ordersPagination.update((p) => ({ ...p, currentPage: 1 }));
  }

  public printOrder(orderId: number): void {
    this.orderService
      .printOrder(orderId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fileBlob: Blob) => {
          const fileUrl = URL.createObjectURL(fileBlob);
          window.open(fileUrl, '_blank');
          setTimeout(() => URL.revokeObjectURL(fileUrl), 10_000);
        },
        error: () => this.handleOpenErrorModal(),
      });
  }

  private updatePagination(paginationConfig: Partial<TablePaginationConfig>): void {
    this.ordersPagination.update((prevState) => ({
      ...prevState,
      ...paginationConfig,
    }));
  }

  private handleOpenErrorModal(): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        success: false,
        title: 'orders.table.errors.title',
        description: 'orders.table.errors.ordersLoadFail',
      },
    });
  }

  ngOnDestroy(): void {
    this.removeScrollbarFix();
  }
}
